import React from 'react'
import { formatMoney } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { MonthBand } from './MonthCard'

interface YearPulseProps {
  bands: MonthBand[]
  currency: string
}

/**
 * A wide editorial sparkline: smooth OTB curve overlaid on STLY trace,
 * with month tick labels along the bottom. Display-only.
 */
export function YearPulse({ bands, currency }: YearPulseProps) {
  if (bands.length === 0) return null

  const width = 1000
  const height = 160
  const padX = 24
  // padTop leaves headroom for the peak value callout, which is drawn 12px above the
  // peak point (itself at y = padTop). Too small a padTop pushes that label above the
  // viewBox and the card's overflow-hidden clips it.
  const padTop = 32
  const padBottom = 30
  const innerW = width - padX * 2
  const innerH = height - padTop - padBottom

  const maxRev = Math.max(
    ...bands.map((b) => b.otb),
    ...bands.map((b) => b.stly),
    ...bands.map((b) => b.lyEnd),
    1
  )

  const x = (i: number) =>
    bands.length === 1 ? padX + innerW / 2 : padX + (i / (bands.length - 1)) * innerW
  const y = (v: number) => padTop + innerH - (v / maxRev) * innerH

  const otbPath = bezierPath(bands.map((b, i) => ({ x: x(i), y: y(b.otb) })))
  const stlyPath = bezierPath(
    bands.map((b, i) => ({ x: x(i), y: y(b.hasStlyData ? b.stly : 0) }))
  )
  const lyEndPath = bezierPath(
    bands.map((b, i) => ({ x: x(i), y: y(b.hasLyEndData ? b.lyEnd : 0) }))
  )

  // OTB area fill path (close to bottom)
  const otbArea = `${otbPath} L ${x(bands.length - 1)} ${padTop + innerH} L ${x(0)} ${padTop + innerH} Z`

  const peak = bands.reduce(
    (best, b, i) => (b.otb > best.otb ? { otb: b.otb, idx: i, label: b.monthShort } : best),
    { otb: -Infinity, idx: 0, label: '' }
  )

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-border/60 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">
            Year Pulse
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Monthly accommodation revenue · OTB versus STLY and last year final
          </p>
        </div>
        <Legend />
      </div>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block h-auto w-full"
        preserveAspectRatio="none"
        aria-label="Year pulse sparkline"
      >
        <defs>
          <linearGradient id="otb-gradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0d9488" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* gridlines: quartiles */}
        {[0.25, 0.5, 0.75].map((q) => (
          <line
            key={q}
            x1={padX}
            x2={width - padX}
            y1={padTop + innerH * q}
            y2={padTop + innerH * q}
            className="stroke-slate-100"
            strokeWidth={1}
          />
        ))}

        {/* OTB area */}
        <path d={otbArea} fill="url(#otb-gradient)" />

        {/* LY final trace (faintest) */}
        <path
          d={lyEndPath}
          fill="none"
          className="stroke-slate-300"
          strokeWidth={1.25}
          strokeDasharray="2 3"
        />

        {/* STLY trace */}
        <path
          d={stlyPath}
          fill="none"
          className="stroke-slate-500"
          strokeWidth={1.5}
          strokeDasharray="4 3"
        />

        {/* OTB line (hero) */}
        <path
          d={otbPath}
          fill="none"
          className="stroke-teal-600"
          strokeWidth={2.25}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* OTB points */}
        {bands.map((b, i) => (
          <circle
            key={b.month}
            cx={x(i)}
            cy={y(b.otb)}
            r={i === peak.idx ? 4.5 : 2.75}
            className={cn(i === peak.idx ? 'fill-teal-600' : 'fill-white stroke-teal-600')}
            strokeWidth={i === peak.idx ? 0 : 1.5}
          />
        ))}

        {/* Peak callout */}
        {peak.otb > 0 && (
          <g transform={`translate(${x(peak.idx)}, ${y(peak.otb) - 12})`}>
            <text
              textAnchor="middle"
              className="fill-teal-700 font-semibold"
              style={{ fontSize: 11, letterSpacing: 0.4 }}
            >
              {formatMoney(peak.otb, currency)}
            </text>
          </g>
        )}

        {/* Month labels */}
        {bands.map((b, i) => (
          <text
            key={`${b.month}-label`}
            x={x(i)}
            y={height - 8}
            textAnchor="middle"
            className="fill-slate-400 uppercase"
            style={{ fontSize: 9, letterSpacing: 1.4, fontWeight: 600 }}
          >
            {b.monthShort}
          </text>
        ))}
      </svg>
    </div>
  )
}

function Legend() {
  return (
    <div className="flex items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      <LegendDot color="bg-teal-600" label="OTB" />
      <LegendDot color="bg-slate-500" dashed label="STLY" />
      <LegendDot color="bg-slate-300" dashed label="LY final" />
    </div>
  )
}

function LegendDot({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          'inline-block h-2 w-3',
          dashed ? `border-t-2 border-dashed ${color.replace('bg-', 'border-')}` : `rounded-full ${color}`
        )}
      />
      {label}
    </span>
  )
}

/**
 * Build a smooth cubic-bezier path through a set of points. Catmull-Rom-ish:
 * keeps a relaxed shape without overshooting peaks. Pure geometry, no math on data.
 */
function bezierPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`

  const tension = 0.2
  let d = `M ${points[0].x} ${points[0].y}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] ?? p2
    const cp1x = p1.x + (p2.x - p0.x) * tension
    const cp1y = p1.y + (p2.y - p0.y) * tension
    const cp2x = p2.x - (p3.x - p1.x) * tension
    const cp2y = p2.y - (p3.y - p1.y) * tension
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
  }
  return d
}
